import * as d3 from 'd3';

import {ASSEMBLY_CONSTRUCT_AXIS_ADDITIONAL_HEIGHT} from 'constants/MapsConstants';
import PolymerAxisView, {PolymerAxisViewOptions} from 'molbio/maps/linear/PolymerAxisView';
import Type from 'molbio/maps/Type';
import * as sequtil from 'molbio/SeqUtil';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {getTranslation} from 'util/getTransform';

class ProteinAxisView extends PolymerAxisView {
  static override initClass(): void {
    this.prototype.className = 'proteinAxis';
    this.prototype.setting = vo.AA_SEQUENCE_AXIS.name;
  }

  override initialize(options: PolymerAxisViewOptions): void {
    super.initialize(options);
    if (this._hasFragmentRegions()) {
      // When displaying fragment regions, override layout for added height.
      this.layout = this.model.get('layout').AssemblyConstructAxis;
    }
  }

  getBasesType(): Type {
    return this.model.get('layout').AminoAcids.get('type');
  }

  _hasFragmentRegions(): boolean {
    return this.model.get('protein').fragments().length > 0;
  }

  _drawAssemblyFragments(): void {
    const fragments = this.model.get('protein').fragments();
    const aaSequenceLength = this.model.get('protein').length();
    const root = this.getAxisRoot();
    root.append('g').attr('id', 'fragments');
    for (const {start, end, color} of fragments) {
      const sliceLength = sequtil.length(this.slice.start(), this.slice.end(), aaSequenceLength);
      const sliceOverlaps = sequtil.getRangeOverlap(start, end, this.slice.start(), this.slice.end());
      if (sliceOverlaps.length > 0) {
        for (const [sliceOverlapStart, sliceOverlapEnd] of sliceOverlaps) {
          // Length in base pairs.
          const sliceOverlapLength = sequtil.length(sliceOverlapStart, sliceOverlapEnd, aaSequenceLength);
          const overlapWidthRatio = sliceOverlapLength / sliceLength;
          const width = this.slice.get('width')! * overlapWidthRatio;

          const residueIndexToTranslateX = (residueIndex: number): number => {
            // Distance from slice start in base pairs.
            let translateIndex: number;
            if (this.slice.start() === residueIndex) {
              translateIndex = 0;
            } else {
              translateIndex = sequtil.length(this.slice.start(), residueIndex, aaSequenceLength);
            }
            // How far from the beginning of the slice basePair is, relative to the size of the whole slice
            const ratio = translateIndex / sliceLength;
            return this.slice.get('width')! * ratio - this.getTypeOffset();
          };

          // Move the ticks down to make space for the larger axis. This does need to be a function
          // instead of an arrow function so that `this` being selected is the tick instead of the view.
          root.selectAll('.tick').attr('transform', function () {
            const translation = getTranslation(d3.select(this).attr('transform'));
            return `translate(${translation[0]},${ASSEMBLY_CONSTRUCT_AXIS_ADDITIONAL_HEIGHT})`;
          });
          // Draw the tick lines.
          root.selectAll('.tick line').style('stroke-width', 1).style('stroke', 'black');

          root
            .select('#fragments')
            .append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('z', 0)
            .attr('width', width)
            .attr('height', 12)
            .style('fill', color)
            .attr('transform', `translate(${residueIndexToTranslateX(sliceOverlapStart)}, 0)`)
            .classed('linear-axis-piece', true);
        }
      }
    }
  }

  override _performAdditionalUpdates(): void {
    if (this._hasFragmentRegions()) {
      this._drawAssemblyFragments();
    } else {
      d3.select(this.el).selectAll('.axis .tick line').style('stroke-width', 1).style('stroke', 'black');
    }
  }
}
ProteinAxisView.initClass();

export default ProteinAxisView;
